/* eslint-disable indent, no-param-reassign,no-plusplus, no-unused-vars */
import axios from 'axios';
import Vue from 'vue';
import uniq from 'lodash/uniq';
// import sortBy from 'lodash/sortBy';
// import difference from 'lodash/difference';
// import differenceBy from 'lodash/differenceBy';
import groupBy from 'lodash/groupBy';
import some from 'lodash/some';
// import stats from 'stats-lite';
import abbreviateNumber from '../../util/abbreviateNumber';
import cacheAxios from '../../util/cacheAxios';

const econPredictorAxios = axios.create();
const cache = new Map();
cacheAxios(econPredictorAxios, cache);

econPredictorAxios.interceptors.response.use(
  response => response,
  (error) => {
    if (error && error.response && error.response.status === 401) {
      setTimeout(() => {
        window.location.href = '/sign/in?source=/aims';
      }, 500);
    }
  },
);

function buildYaxis(lbl = '', gridIndex = 0) {
  const yAxis = {
    splitLine: {
      show: false,
    },
    gridIndex,
    axisLabel: {
      type: 'value',
      formatter: (label) => {
        if (label >= 0) {
          return `${lbl} ${abbreviateNumber(label)}`;
        }
        return `(${lbl} ${abbreviateNumber(-label)})`;
      },
    },
  };
  return yAxis;
}

function buildSeries(
  countries,
  dataCountryCode,
  data,
  userCountryCode,
  index = -1,
  showSymbol = true,
  justUseValue = true,
) {
  const name = countries.find(c => c.countryCode === dataCountryCode)
    .countryName;
  const lineStyle = {
    normal: {
      width: 2,
    },
  };
  if (dataCountryCode === userCountryCode) {
    lineStyle.normal.width = 5;
  }
  let dataS;
  if (justUseValue) {
    dataS = data.map(d => [d.date, d.value]);
  } else {
    dataS = data.map(d => [d.date, d.usdValue || d.value]);
  }

  const built = {
    name,
    type: 'line',
    showSymbol,
    lineStyle,
    xAxisIndex: index,
    yAxisIndex: index,
    data: dataS,
  };
  if (index === -1) {
    built.xAxisIndex = 0;
    built.yAxisIndex = 0;
    built.label = {
      normal: {
        show: false,
        formatter: '{a}',
      },
      emphasis: {
        show: true,
        formatter: '{a}',
      },
    };
  }
  return built;
}

const singleGrid = [
  {
    right: '12%',
    bottom: '11%',
  },
];
const legend = {
  bottom: '10%',
  right: '1%',
  orient: 'vertical',
  align: 'right',
};
const dataZoom = [
  {
    type: 'inside',
    show: true,
    realtime: true,
    start: 30,
    end: 100,
  },
  {
    type: 'slider',
    realtime: true,
    start: 30,
    end: 100,
  },
];

export default {
  state: {
    chartLoading: false,
    regionsGroup: [],
    regionSelected: '',
    countriesForPlot: [],
    economyIndicatorSelected: 'NGDPD',
    economyIndicatorHistoryMap: {},
    imfIndicatorMetas: {},
    chartOptions: {
      title: {
        left: 'center',
        top: '1%',
      },
      backgroundColor: '#fff',
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999',
          },
        },
      },
      toolbox: {
        feature: {
          // dataZoom: {
          //   yAxisIndex: 'none',
          // },
          dataView: {
            optionToContent(opt) {
              const axisData = opt.xAxis[0].data;
              const series = opt.series;
              let table = '<table style="text-align:left;user-select:all;border-collapse: collapse;"><tbody><tr><td style="border: 1px solid #ddd;padding: 8px;">Date</td>';

              for (let i = 0; i < series.length; i++) {
                table += `<td style="border: 1px solid #ddd;padding: 8px;">${series[i].name}</td>`;
              }
              table += '</tr>';

              for (let i = 0, l = axisData.length; i < l; i++) {
                  table += `<tr><td style="border: 1px solid #ddd;padding: 8px;">${axisData[i]}</td>`;
                  for (let j = 0; j < series.length; j++) {
                    if (series[j].data[i] && series[j].data[i].length === 2) {
                      table += `<td style="border: 1px solid #ddd;padding: 8px;">${series[j].data[i][1]}</td>`;
                    } else {
                      table += '<td style="border: 1px solid #ddd;padding: 8px;"></td>';
                    }
                  }
                  table += '</tr>';
              }
              table += '</tbody></table>';
              return table;
            },
            readOnly: true,
            title: 'Data View',
            lang: ['Data View', 'Close', 'Refresh'],
          },
          saveAsImage: {
            show: true,
            title: 'save',
          },
        },
      },
      dataZoom,
      series: [],
      xAxis: [
        {
          type: 'time',
          min: 'dataMin',
          max: 'dataMax',
          data: [],
          splitLine: {
            show: false,
          },
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      grid: singleGrid,
      legend,
      yAxis: [
        {
          splitLine: {
            show: false,
          },
          axisLabel: {
            type: 'value',
            formatter: (label) => {
              if (label >= 0) {
                return `US$${abbreviateNumber(label)}`;
              }
              return `(US$${abbreviateNumber(-label)})`;
            },
          },
        },
      ],
    },
  },
  mutations: {
    setImfIndicatorMetas(state, metas) {
      state.imfIndicatorMetas = metas;
    },
    setRegionsGroup(state, regionsGrp) {
      state.regionsGroup = regionsGrp;
    },
    setChartLoading(state, loading) {
      state.chartLoading = loading;
    },
    addEconomyIndicatorHistoryMap(state, maps) {
      Object.keys(maps)
        .forEach((k) => {
          Vue.set(state.economyIndicatorHistoryMap, k, maps[k]);
        });
    },
    setEconomyIndicatorSelected(state, indicator) {
      state.economyIndicatorSelected = indicator;
    },
    setUserCountry(state, country) {
      state.userCountry = country;
    },
    clearCountriesForPlot(state) {
      state.countriesForPlot = [];
    },
    removeCountriesForPlot(state, countries) {
      state.countriesForPlot = state.countriesForPlot.filter(
        c => !countries.find(cc => cc === c.countryCode),
      );
    },
    appendCountriesForPlot(state, countries) {
      countries.forEach((c) => {
        if (!some(state.countriesForPlot, ['countryCode', c.countryCode])) {
          state.countriesForPlot.push(c);
        }
      });
    },
    setRegionSelected(state, region) {
      state.regionSelected = region;
    },
    plotSingleChart(state, val) {
      const title = {
        ...state.chartOptions.title,
        text: val.title,
        top: '1%',
        left: 'center',
      };
      const xAxis = {
        ...state.chartOptions.xAxis,
        data: val.xAxisData,
      };
      const series = val.series;
      const legendSingle = {
        ...legend,
        data: val.legendData,
      };
      state.chartOptions = {
        ...state.chartOptions,
        title,
        xAxis,
        grid: singleGrid,
        yAxis: val.yAxis,
        legend: legendSingle,
        dataZoom,
        series,
        clear: true,
      };
    },
    plotMultipleChart(state, {
      xAxis,
      series,
      yAxis,
      grid,
      title,
    }) {
      state.chartOptions = {
        ...state.chartOptions,
        xAxis,
        yAxis,
        series,
        grid,
        title,
        legend: {
          data: [],
        },
        dataZoom: [],
        clear: true,
      };
    },
  },
  actions: {
    saveEconLocationList(_, list) {
      return econPredictorAxios.post('/aims/api/economy/saveEconLocationList', list);
    },

    retrieveEconLocationList() {
      return econPredictorAxios.get('/aims/api/economy/userEconLocationList');
    },

    removeEconLocationList(_, listName) {
      return econPredictorAxios.delete(`/aims/api/economy/userEconLocationList/${listName}`);
    },

    getImfIndicatorMetas({
      commit,
    }) {
      econPredictorAxios
        .get('/aims/api/economy/imfIndicatorsMeta')
        .then((res) => {
          if (res && res.data) {
            commit('setImfIndicatorMetas', res.data);
          }
        });
    },
    getEconomyIndicatorHistory({
      state,
      commit,
      dispatch,
    }, countries) {
      commit('setChartLoading', true);
      const fCountries = countries
        .filter(
          c =>
          !(`${c}|${state.economyIndicatorSelected}` in
            state.economyIndicatorHistoryMap),
        );
      if (fCountries.length > 0) {
        econPredictorAxios.post('/aims/api/economy/imfIndicators', { countries: fCountries, indicator: state.economyIndicatorSelected })
          .then((res) => {
            commit('addEconomyIndicatorHistoryMap', res.data);
            if (
              state.imfIndicatorMetas[state.economyIndicatorSelected].separate
            ) {
              dispatch('prepareMultipleCharts', countries);
            } else {
              dispatch('prepareSingleChart', countries);
            }
          });
      } else if (
        state.imfIndicatorMetas[state.economyIndicatorSelected].separate
      ) {
        dispatch('prepareMultipleCharts', countries);
      } else {
        dispatch('prepareSingleChart', countries);
      }
    },
    prepareSingleChart({
      state,
      rootState,
      commit,
    }, countries) {
      const userCountry = rootState.currentUserCountry;
      let xAxisData = [];
      let series = [];
      let yAxis;
      const indicators = countries
        .map(c => ({
          countryCode: c,
          indicators: state.economyIndicatorHistoryMap[
            `${c}|${state.economyIndicatorSelected}`
            ] || [],
        }))
        .filter(c => c.indicators.length > 0);
      let cond1 = false;
      let cond2 = false;
      let cond3 = false;
      if (indicators.length > 0) {
        if (indicators[0].indicators.length > 0) {
          cond1 = state.imfIndicatorMetas[indicators[0].indicators[0].imfCode].units.toLowerCase().includes('u.s. dollars');
          cond2 = state.imfIndicatorMetas[indicators[0].indicators[0].imfCode].units.toLowerCase().includes('us dollars');
          cond3 = state.imfIndicatorMetas[indicators[0].indicators[0].imfCode].units.toLowerCase().includes('billions') || state.imfIndicatorMetas[indicators[0].indicators[0].imfCode].units.toLowerCase().includes('millions');
          let lbl = '';
          if (cond1 || cond2) {
            lbl = 'US';
          }
          yAxis = buildYaxis(lbl);
        }
      }
      if (!yAxis) {
        yAxis = buildYaxis();
      }
      indicators.forEach((i) => {
        i.indicators.forEach(id => xAxisData.push(id.date));
      });
      xAxisData = uniq(xAxisData)
        .sort();
      series = indicators.map(i =>
        buildSeries(
          state.countriesForPlot,
          i.countryCode,
          i.indicators,
          userCountry ? userCountry.countryCode : '',
        ),
      );
      const legendData = series.map(s => s.name);
      let title = state.imfIndicatorMetas[state.economyIndicatorSelected].indicator;
      if ((!cond1 && !cond2) || cond3) {
        title += ` (${state.imfIndicatorMetas[state.economyIndicatorSelected].units})`;
      }
      commit('plotSingleChart', {
        title,
        xAxisData,
        series,
        legendData,
        userCountry,
        yAxis,
      });
      commit('setChartLoading', false);
    },
    prepareMultipleCharts({
        state,
        commit,
      },
      countries,
    ) {
      let xAxis = [];
      let series = [];
      let yAxis = [];
      let title = [];
      const indicators = countries
        .map(c => ({
          countryCode: c,
          indicators: state.economyIndicatorHistoryMap[
            `${c}|${state.economyIndicatorSelected}`
            ] || [],
        }))
        .filter(c => c.indicators.length > 0);
      let count = 0;
      xAxis = indicators.map((i) => {
        const xAxisData = [];
        i.indicators.forEach(id => xAxisData.push(id.date));
        return {
          ...state.chartOptions.xAxis[0],
          gridIndex: count++,
          data: xAxisData,
        };
      });
      let index = 0;
      series = indicators.map(i =>
        buildSeries(
          state.countriesForPlot,
          i.countryCode,
          i.indicators,
          '',
          index++,
          i.indicators.length < 2,
        ),
      );
      count = 0;
      yAxis = indicators.map((i) => {
        let cond1 = false;
        let cond2 = false;
        if (i.indicators.length > 0) {
          cond1 = state.imfIndicatorMetas[indicators[0].indicators[0].imfCode].units.toLowerCase().includes('u.s. dollars');
          cond2 = state.imfIndicatorMetas[indicators[0].indicators[0].imfCode].units.toLowerCase().includes('us dollars');
        }
        if (cond1 || cond2) {
          return buildYaxis('US', count++);
        }
        return buildYaxis('', count++);
      });
      count = 0;
      const sqrt = Math.sqrt(indicators.length);
      const rows = Math.round(sqrt);
      const cols = Math.ceil(sqrt);
      const width = 1 / cols * 93 - 4; // eslint-disable-line
      const height = 1 / rows * 93 - 4; // eslint-disable-line
      const grid = indicators.map((i) => {
        const g = {};
        g.left = `${count % cols * width + (count % cols + 1) * 4}%`; // eslint-disable-line
        g.top =
          `${Math.floor(count / cols) * height + (Math.floor(count / cols) + 1) * 4 + 4}%`; // eslint-disable-line
        g.width = `${width}%`; // eslint-disable-line
        g.height = `${height}%`; // eslint-disable-line
        g.containLabel = true;
        count++;
        return g;
      });
      count = 0;
      title = indicators
        .map((i) => {
          let name = state.countriesForPlot.find(c => c.countryCode === i.countryCode)
          .countryName;
          if (i.indicators[0].unit.length > 0) {
            name += ` (${i.indicators[0].unit})`;
          }
          return name;
        },
        )
        .map((c) => {
          const t = {
            top: `${Math.floor(count / cols) * height + (Math.floor(count / cols) + 1) * 4 + 2}%`, // eslint-disable-line
            left: `${count % cols * width + (count % cols + 1) * 4 + 0.5 * width}%`, // eslint-disable-line
            text: c,
            textAlign: 'center',
            textStyle: {
              fontSize: 14,
              fontWeight: 'normal',
            },
          };
          count++;
          return t;
        });
      const cond1 = state.imfIndicatorMetas[state.economyIndicatorSelected].units.toLowerCase().includes('u.s. dollars');
      const cond2 = state.imfIndicatorMetas[state.economyIndicatorSelected].units.toLowerCase().includes('us dollars');
      const cond3 = state.imfIndicatorMetas[indicators[0].indicators[0].imfCode].units.toLowerCase().includes('billions') || state.imfIndicatorMetas[indicators[0].indicators[0].imfCode].units.toLowerCase().includes('millions');
      let text = state.imfIndicatorMetas[state.economyIndicatorSelected].indicator;
      if ((!cond1 && !cond2 &&
        state.imfIndicatorMetas[state.economyIndicatorSelected].units.length > 0) || cond3) {
        text += ` (${state.imfIndicatorMetas[state.economyIndicatorSelected].units})`;
      }
      title.push({
        top: '1%',
        left: 'center',
        text,
      });
      commit('plotMultipleChart', {
        title,
        xAxis,
        series,
        yAxis,
        grid,
      });
      commit('setChartLoading', false);
    },
    getRegionsGroup({
      commit,
    }) {
      econPredictorAxios.get('/aims/api/economy/regionsGrp')
        .then((res) => {
          if (res.data) {
            commit('setRegionsGroup', res.data);
          }
        });
    },
  },
  getters: {
    imfIndicatorsTree(state) {
      const groupped = groupBy(Object.keys(state.imfIndicatorMetas).map(e => state.imfIndicatorMetas[e]), 'group');
      const tree = Object.keys(groupped)
        .map((key) => {
          const children = groupped[key].map(c => ({
            ...c,
            code: c.imfCode,
            label: c.indicator,
          }));
          children.sort((a, b) => a.rank - b.rank);
          const obj = {
            label: key,
            code: key,
            children,
          };
          return obj;
        });
      return tree;
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/econPredictorModule.js